'use client'

import posthog from 'posthog-js'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useRef } from 'react'
import { capture } from '@/features/tracking'
import { PETA_CAMPAIGN_QUERY_PARAM } from './core'
import type { PetaCampaignAttribution, PetaCampaignEvaluation } from './types'

const PetaCampaignContext = createContext<PetaCampaignAttribution | null>(null)
const CAMPAIGN_PROPERTY_KEYS = [
  'campaign_id',
  'campaign_source',
  'campaign_medium',
  'campaign_name',
] as const

function registerCampaign(attribution: PetaCampaignAttribution) {
  posthog.register(attribution)
}

function unregisterCampaign() {
  for (const key of CAMPAIGN_PROPERTY_KEYS) posthog.unregister(key)
}

export function PetaCampaignAnalyticsProvider({
  attribution,
  children,
}: {
  attribution: PetaCampaignAttribution | null
  children: ReactNode
}) {
  useEffect(() => {
    if (attribution) {
      registerCampaign(attribution)
      return
    }

    const isCampaignLanding = new URLSearchParams(window.location.search).has('campaign_access')
    if (!isCampaignLanding) unregisterCampaign()
  }, [attribution])

  return <PetaCampaignContext.Provider value={attribution}>{children}</PetaCampaignContext.Provider>
}

export function usePetaCampaignAttribution() {
  return useContext(PetaCampaignContext)
}

export function PetaCampaignLandingTracker({ evaluation }: { evaluation: PetaCampaignEvaluation }) {
  const tracked = useRef(false)

  useEffect(() => {
    if (tracked.current) return
    tracked.current = true

    const url = new URL(window.location.href)
    url.searchParams.delete(PETA_CAMPAIGN_QUERY_PARAM)
    const sanitizedUrl = `${url.origin}${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, '', sanitizedUrl)

    if (evaluation.result === 'valid') registerCampaign(evaluation.attribution)

    capture('campaign:landing', {
      ...evaluation.attribution,
      $current_url: sanitizedUrl,
      access_result: evaluation.result,
      host: window.location.host,
    })
  }, [evaluation])

  return null
}
